import { useEffect, useRef, useState } from "react";
import { Bold, Italic, List, ListOrdered, Link2, Code, Eye, Pilcrow } from "lucide-react";

/**
 * Einfacher Rich-Text-Composer für E-Mail-Inhalte.
 * - WYSIWYG via contentEditable (document.execCommand — pragmatisch, kein TipTap-Overhead)
 * - Toolbar: Fett / Kursiv / Listen / Link / Absatz
 * - Variablen-Chips: per Klick ins Feld einfügen
 * - Toggle „Quelltext" für Power-User
 */
export default function RichTextComposer({
  value = "",
  onChange,
  variables = [],
  placeholder = "Hier deine E-Mail schreiben…",
  testId = "rich-text-composer",
}) {
  const editorRef = useRef(null);
  const [showSource, setShowSource] = useState(false);
  const [sourceValue, setSourceValue] = useState(value);

  // Sync external value → editor (nur wenn sich der Wert wirklich von außen geändert hat,
  // z.B. beim Template-Wechsel — sonst würde der Cursor beim Tippen springen)
  useEffect(() => {
    if (showSource) {
      setSourceValue(value);
      return;
    }
    const el = editorRef.current;
    if (el && el.innerHTML !== value) {
      el.innerHTML = value || "";
    }
  }, [value, showSource]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    editorRef.current?.focus();
    onChange?.(editorRef.current?.innerHTML || "");
  };

  const insertAtCursor = (html) => {
    if (showSource) {
      // Im Quelltext-Modus: an Cursor-Position der Textarea einfügen
      const ta = editorRef.current;
      if (!ta) return;
      const start = ta.selectionStart || 0;
      const end = ta.selectionEnd || 0;
      const newVal = sourceValue.slice(0, start) + html + sourceValue.slice(end);
      setSourceValue(newVal);
      onChange?.(newVal);
      // Cursor hinter den eingefügten Text setzen
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(start + html.length, start + html.length);
      });
      return;
    }
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, html);
    onChange?.(editorRef.current?.innerHTML || "");
  };

  const handleLink = () => {
    const url = window.prompt("Link-URL eingeben:");
    if (!url) return;
    exec("createLink", url);
  };

  const handleSourceChange = (e) => {
    setSourceValue(e.target.value);
    onChange?.(e.target.value);
  };

  const ToolButton = ({ onClick, title, children, ...props }) => (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}  // verhindert Cursor-Verlust
      title={title}
      className="inline-flex items-center justify-center w-7 h-7 rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground transition disabled:opacity-30 disabled:cursor-not-allowed"
      {...props}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid={testId}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1 border-b border-border bg-muted/30">
        <ToolButton onClick={() => exec("bold")} title="Fett (Cmd/Ctrl+B)" disabled={showSource} data-testid="rt-bold">
          <Bold size={13} />
        </ToolButton>
        <ToolButton onClick={() => exec("italic")} title="Kursiv (Cmd/Ctrl+I)" disabled={showSource} data-testid="rt-italic">
          <Italic size={13} />
        </ToolButton>
        <span className="w-px h-4 bg-border mx-1" />
        <ToolButton onClick={() => exec("formatBlock", "p")} title="Absatz" disabled={showSource} data-testid="rt-paragraph">
          <Pilcrow size={13} />
        </ToolButton>
        <ToolButton onClick={() => exec("insertUnorderedList")} title="Aufzählung" disabled={showSource} data-testid="rt-ul">
          <List size={13} />
        </ToolButton>
        <ToolButton onClick={() => exec("insertOrderedList")} title="Nummerierte Liste" disabled={showSource} data-testid="rt-ol">
          <ListOrdered size={13} />
        </ToolButton>
        <ToolButton onClick={handleLink} title="Link einfügen" disabled={showSource} data-testid="rt-link">
          <Link2 size={13} />
        </ToolButton>
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => setShowSource(s => !s)}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wide transition ${
            showSource ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
          title="Quelltext-Modus umschalten"
          data-testid="rt-source-toggle"
        >
          {showSource ? <Eye size={10} /> : <Code size={10} />}
          {showSource ? "Visuell" : "Quelltext"}
        </button>
      </div>

      {/* Editor */}
      {showSource ? (
        <textarea
          ref={editorRef}
          value={sourceValue}
          onChange={handleSourceChange}
          rows={10}
          className="w-full px-3 py-2.5 text-xs font-mono bg-transparent outline-none resize-y min-h-[180px]"
          spellCheck={false}
          data-testid={`${testId}-source`}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => onChange?.(e.currentTarget.innerHTML)}
          className="prose prose-sm max-w-none dark:prose-invert px-3 py-2.5 text-sm outline-none min-h-[180px] focus:ring-1 focus:ring-primary/20"
          data-placeholder={placeholder}
          data-testid={`${testId}-editor`}
          style={{ minHeight: 180 }}
        />
      )}

      {/* Variablen-Chips */}
      {variables.length > 0 && (
        <div className="px-2 py-1.5 border-t border-border bg-muted/20 flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Variablen:</span>
          {variables.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => insertAtCursor(`{{${v}}}`)}
              onMouseDown={(e) => e.preventDefault()}
              className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-mono bg-card border border-border text-foreground hover:border-primary hover:bg-primary/10 transition"
              title={`{{${v}}} einfügen`}
              data-testid={`rt-var-${v}`}
            >
              {`{{${v}}}`}
            </button>
          ))}
        </div>
      )}

      {/* Placeholder Style */}
      <style>{`
        [contenteditable=true]:empty:before {
          content: attr(data-placeholder);
          color: var(--muted-foreground, #94a3b8);
          pointer-events: none;
        }
        [contenteditable=true] p { margin: 0 0 0.5em 0; }
        [contenteditable=true] ul, [contenteditable=true] ol { padding-left: 1.4em; margin: 0.4em 0; }
      `}</style>
    </div>
  );
}
